import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import {
  convertQuestionMappings,
  convertVisibleIf,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
    40,
  ]
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  const visibleIfConditionsInit = convertVisibleIf({
    // 2-6-3-2 第二支出(q16)選項11=(12)無第二 → 隱藏 2-6-3-3 第三支出
    q17: '{q16} <> 11',
    // 3-1-1/3-1-2 國籍：選項2=(1-2)本國籍具新住民身份 → 顯示原國籍；選項4=(2)外國籍 → 顯示國籍
    q20: '{q19} = 2',
    q21: '{q19} = 4',
    q23: '{q22} = 2',
    q24: '{q22} = 4',
    // 3-4-1-1 父親工作狀況：1有工作 2無工作 3不清楚或已死亡
    q29: '{q28} = 1',
    q30: '{q28} = 1',
    q31: '{q28} = 1',
    q32: '{q28} = 1',
    q33: '({q28} = 1) or ({q28} = 2)',
    q34: '({q28} = 1) or ({q28} = 2)',
    q35: '{q28} = 2',
    // 3-4-1-2 母親工作狀況（同上鏡像）
    q37: '{q36} = 1',
    q38: '{q36} = 1',
    q39: '{q36} = 1',
    q40: '{q36} = 1',
    q41: '({q36} = 1) or ({q36} = 2)',
    q42: '({q36} = 1) or ({q36} = 2)',
    q43: '{q36} = 2',
    // 3-4-1-1 父親工作狀況(q28)選(3)不清楚或已死亡 → 隱藏 3-5 父親育嬰假整段(q44~q51)
    // 3-5-1-1 父親育嬰假：1有 2沒有 3不清楚；3-5-4-1 返回職場：1有 2沒有 3請假中
    q44: '{q28} <> 3',
    q45: '({q28} <> 3) and ({q44} = 1)',
    q46: '({q28} <> 3) and ({q44} = 1)',
    q47: '({q28} <> 3) and ({q44} = 1)',
    q48: '({q28} <> 3) and ({q44} = 1) and ({q47} = 1)',
    q49: '({q28} <> 3) and ({q44} = 1) and ({q47} = 1)',
    q50: '({q28} <> 3) and ({q44} = 1) and ({q47} = 2)',
    q51: '({q28} <> 3) and ({q44} = 2)',
    // 3-4-1-2 母親工作狀況(q36)選(3)不清楚或已死亡 → 隱藏 3-5 母親育嬰假整段(q52~q59)（同上鏡像）
    q52: '{q36} <> 3',
    q53: '({q36} <> 3) and ({q52} = 1)',
    q54: '({q36} <> 3) and ({q52} = 1)',
    q55: '({q36} <> 3) and ({q52} = 1)',
    q56: '({q36} <> 3) and ({q52} = 1) and ({q55} = 1)',
    q57: '({q36} <> 3) and ({q52} = 1) and ({q55} = 1)',
    q58: '({q36} <> 3) and ({q52} = 1) and ({q55} = 2)',
    q59: '({q36} <> 3) and ({q52} = 2)',
    // 4-5 變更戶籍：JSON 選項1=「否」、選項2=「是(填空 o_type6)」→ 選「是」值為 "other"；選否只跳過 4-6
    q69: '{q68} = "other"',
    // 4-7-0/4-8-0/4-9-0 閘門：選項1=(0)未曾使用、選項2=(1)曾使用過 → 曾使用過才顯示經驗題
    q71: '{q70} = 2',
    q73: '{q72} = 2',
    q75: '{q74} = 2',
    // 4-10 主要照顧者四分流：1家人 2保母 3托嬰中心 4幼兒園
    q77: '{q76} = 1',
    q78: '({q76} = 1) and ({q77} = 1)',
    q79: '({q76} = 1) and ({q77} = 2)',
    q80: '({q76} = 1) and ({q77} = 2)',
    q81: '{q76} = 2',
    q82: '{q76} = 2',
    q83: '{q76} = 2',
    q84: '{q76} = 2',
    q85: '{q76} = 2',
    q86: '{q76} = 2',
    q87: '{q76} = 2',
    q88: '{q76} = 2',
    // 4-10-2-6 費用子題：父題(q88)選項1~6=(2)副食品/(3)延托/(4)獎金/(5-1)(5-2)(5-3)其他，選項7=沒有其他費用(互斥)
    q89: '({q76} = 2) and ({q88} anyof [1])',
    q90: '({q76} = 2) and ({q88} anyof [2])',
    q91: '({q76} = 2) and ({q88} anyof [3])',
    // 正式版把 (5-1)(5-2)(5-3) 各拆成「項目」「費用」兩題（q92~q97），
    // 顯示條件與拆分前相同：未勾該筆其他費用就兩題都隱藏
    q92: '({q76} = 2) and ({q88} anyof [4])', // (5-1) 項目
    q93: '({q76} = 2) and ({q88} anyof [4])', // (5-1) 費用
    q94: '({q76} = 2) and ({q88} anyof [5])', // (5-2) 項目
    q95: '({q76} = 2) and ({q88} anyof [5])', // (5-2) 費用
    q96: '({q76} = 2) and ({q88} anyof [6])', // (5-3) 項目
    q97: '({q76} = 2) and ({q88} anyof [6])', // (5-3) 費用
    q98: '{q76} = 3',
    q99: '{q76} = 3',
    q100: '{q76} = 3',
    q101: '{q76} = 3',
    q102: '{q76} = 3',
    q103: '{q76} = 3',
    q104: '{q76} = 3',
    // 4-10-3-5 費用子題：父題(q104)選項1~6=(2)註冊/(3)副食品/(4)延托/(5-1)(5-2)(5-3)其他
    q105: '({q76} = 3) and ({q104} anyof [1])',
    q106: '({q76} = 3) and ({q104} anyof [2])',
    q107: '({q76} = 3) and ({q104} anyof [3])',
    // 同上，(5-1)(5-2)(5-3) 各拆成項目/費用兩題（q108~q113）
    q108: '({q76} = 3) and ({q104} anyof [4])', // (5-1) 項目
    q109: '({q76} = 3) and ({q104} anyof [4])', // (5-1) 費用
    q110: '({q76} = 3) and ({q104} anyof [5])', // (5-2) 項目
    q111: '({q76} = 3) and ({q104} anyof [5])', // (5-2) 費用
    q112: '({q76} = 3) and ({q104} anyof [6])', // (5-3) 項目
    q113: '({q76} = 3) and ({q104} anyof [6])', // (5-3) 費用
    q114: '{q76} = 3',
    q115: '{q76} = 4',
    q116: '{q76} = 4',
    q117: '{q76} = 4',
    q118: '{q76} = 4',
    q119: '{q76} = 4',
    q120: '{q76} = 4',
    q121: '{q76} = 4',
    // 4-10-4-5 費用子題：父題(q121)選項1~6=(2)交通/(3)課後延托/(4)才藝/(5-1)(5-2)(5-3)其他
    // 4-10-4-5 費用子題：父題(q121)選項1~6=(2)交通/(3)課後延托/(4)才藝/(5-1)(5-2)(5-3)其他
    q122: '({q76} = 4) and ({q121} anyof [1])',
    q123: '({q76} = 4) and ({q121} anyof [2])',
    q124: '({q76} = 4) and ({q121} anyof [3])',
    // 正式版把 (5-1)(5-2)(5-3) 各拆成「項目」「費用」兩題（q125~q130），與保母/托嬰中心一致
    q125: '({q76} = 4) and ({q121} anyof [4])', // (5-1) 項目
    q126: '({q76} = 4) and ({q121} anyof [4])', // (5-1) 費用
    q127: '({q76} = 4) and ({q121} anyof [5])', // (5-2) 項目
    q128: '({q76} = 4) and ({q121} anyof [5])', // (5-2) 費用
    q129: '({q76} = 4) and ({q121} anyof [6])', // (5-3) 項目
    q130: '({q76} = 4) and ({q121} anyof [6])', // (5-3) 費用
    q131: '{q76} = 4',
    // 5-2 身心障礙證明選(2)無 → 跳過 5-3
    q138: '{q137} = 1',
    // 5-5：JSON 選項1=「每天都沒吃」、選項2=「有吃(填空 o_type6)」→ 有吃值為 "other"
    q141: '{q140} = "other"',
    // 2-1 父親(1)或母親(2)至少勾一個才顯示 5-9
    q144: '{q6} anyof [1, 2]',
    // 7-1-1 ~ 7-1-20：選(B1)=選項3 → 顯示 B1 題；選(B2)=選項4 → 顯示 B2 題
    q150: '{q149} = 3',
    q151: '{q149} = 4',
    q153: '{q152} = 3',
    q154: '{q152} = 4',
    q156: '{q155} = 3',
    q157: '{q155} = 4',
    q159: '{q158} = 3',
    q160: '{q158} = 4',
    q162: '{q161} = 3',
    q163: '{q161} = 4',
    q165: '{q164} = 3',
    q166: '{q164} = 4',
    q168: '{q167} = 3',
    q169: '{q167} = 4',
    q171: '{q170} = 3',
    q172: '{q170} = 4',
    q174: '{q173} = 3',
    q175: '{q173} = 4',
    q177: '{q176} = 3',
    q178: '{q176} = 4',
    q180: '{q179} = 3',
    q181: '{q179} = 4',
    q183: '{q182} = 3',
    q184: '{q182} = 4',
    q186: '{q185} = 3',
    q187: '{q185} = 4',
    q189: '{q188} = 3',
    q190: '{q188} = 4',
    q192: '{q191} = 3',
    q193: '{q191} = 4',
    q195: '{q194} = 3',
    q196: '{q194} = 4',
    q198: '{q197} = 3',
    q199: '{q197} = 4',
    q201: '{q200} = 3',
    q202: '{q200} = 4',
    q204: '{q203} = 3',
    q205: '{q203} = 4',
    q207: '{q206} = 3',
    q208: '{q206} = 4',
    // 7-1-21 其他服務：未填 → 跳過 B/B1/B2；B 題選項1=B1、選項2=B2
    q210: '{q209} notempty',
    q211: '({q209} notempty) and ({q210} = 1)',
    q212: '({q209} notempty) and ({q210} = 2)',
  })

  // 7-1-21（題號209）開放填答非必填；notRequired 無轉換工具，吃 0-based 原始索引
  const notRequired = [208]

  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 篩出/結束條件
  const triggersConditionsInit = null

  // 帶出: 帶入 (用1開始計算填這裡)
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    sfdSamplerId,
    webAuthType,
  })

  useEffect(() => {
    if (surveyModel) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [surveyModel])

  // 當 surveyModel 建立時，自動捲動到頂端
  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {
    // 參考 P231113152849 或 P231101165555
  }

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    sfdSamplerId,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
